import React from 'react'
import { useSelector } from 'react-redux'
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from 'pure-react-carousel'
import 'pure-react-carousel/dist/react-carousel.es.css'
import { ChevronLeft, ChevronRight } from 'react-feather'
import { Flex, Box, Text } from 'rebass'
import styled from 'particles/themes/styled'
import { useNewTranslation } from 'hooks/index'
import { selectTasks, TaskState, StepState } from 'state/tasks'
import {
    SUBMISSION_VIEW_ENUM,
    BatchSubmissionTaskState,
    BatchSubmissionState,
    SUBMISSION_TASK_STATUS_ENUM,
} from 'state/submissions'
import { selectProfileDetails } from 'state'
import {
    selectCheckCanReviewSubmissionByStep,
    selectIsInternalViewOnlyStepHelper,
    selectSelectedCampaignSummary,
} from 'state/review'
import { theme } from 'theme'
import { RejectOrVerify } from '../ProgressBlock/RejectOrVerify'
import SubmittedAtDisplay from '../ProgressBlock/SubmittedAtDisplay'
import SubmissionContent from '../ProgressBlock/SubmissionContent'
import CommentAndVoteSummary from '../ProgressBlock/CommentAndVoteSummary'
import { NotDoneBlock, InProgressBlock, CompletedBlock } from './NonePendingTaskBlock'
import Divider from 'components/Divider'
import ViewDetailButton from '../ProgressBlock/ViewDetailButton'
import RectangleButton from 'particles/Button/RectangleButton'
import { SubmissionActionWrapper } from '../ProgressBlock/styles'
import { SEARCH_PARAMS, getSearchParams } from 'components/utils/url'
import { useHistory } from 'react-router-dom'
import { ReviewedByDisplay } from '../ProgressBlock/ReviewedByDisplay'
import { spacing } from 'particles/styles'
import EditRequestHistoryButton from '../EditRequestHistory'
import ResubmittedIndicator from '../ResubmittedIndicator'

const CarouselWrapper = styled(CarouselProvider)`
    height: 100%;
    .box {
        position: relative;
        width: 100%;
    }
    .buttonBack {
        position: absolute;
        top: 65%;
        left: 0;
        transform: translateY(-50%);
        border-width: 0px;
        background-color: transparent;
        :disabled {
            background: transparent;
            opacity: 0.4;
        }
        :focus {
            outline: 0;
        }
    }
    .buttonNext {
        position: absolute;
        top: 65%;
        right: 0;
        transform: translateY(-50%);
        border-width: 0px;
        background-color: transparent;
        :disabled {
            background: transparent;
            opacity: 0.4;
        }
        :focus {
            outline: 0;
        }
    }
    .slider {
        margin: 0 ${theme.spacing.xs} 0;
    }
    .max-height {
        height: 100%;
    }
`

const SlideWrapper = styled(Slide)`
    margin: 0 ${theme.spacing.xxs} 0;
    height: 100%;
    .focusRing___1airF.carousel__slide-focus-ring {
        outline: none !important;
    }
`

interface ViewEditRequestCommentsBlockProps {
    hasResubmitted?: boolean
}

const ViewEditRequestCommentsBlock = styled.div<ViewEditRequestCommentsBlockProps>`
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
    align-items: baseline;
    ${({ hasResubmitted }) => (hasResubmitted ? { height: 20 } : undefined)}

    .edit-request-history {
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        -o-text-overflow: ellipsis;
        width: 60%;
    }
`

interface CarouselProps {
    submissionTasks: BatchSubmissionTaskState[]
    submissionUuid: string
    partiposterId: string
}

interface TaskProgressProps {
    submissionTask?: BatchSubmissionTaskState
    task: TaskState | undefined
    currentStepIndex?: number
    submissionUuid?: string
    partiposterId: string
    hasResubmitted?: boolean
}

const TaskProgress: React.FC<TaskProgressProps> = ({
    submissionTask,
    task,
    currentStepIndex,
    submissionUuid,
    partiposterId,
    hasResubmitted,
}) => {
    const [t] = useNewTranslation()
    const profile = useSelector(selectProfileDetails)
    const isInternalViewOnlyStep = useSelector(selectIsInternalViewOnlyStepHelper)
    const checkCanReviewSubmissionByStep = useSelector(selectCheckCanReviewSubmissionByStep)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const history = useHistory()

    const stepIndex = currentStepIndex ? currentStepIndex : 0

    const handleClick = (viewType: SUBMISSION_VIEW_ENUM) => {
        if (submissionTask && submissionUuid) {
            window.scrollTo(0, 0)
            const searchParams = getSearchParams({
                add: [
                    { key: SEARCH_PARAMS.viewType, value: viewType },
                    { key: SEARCH_PARAMS.task, value: submissionTask.task_uuid },
                    { key: SEARCH_PARAMS.step, value: submissionTask.current_progress.step_uuid },
                ],
            })
            history.push(searchParams)
        }
    }

    // Don't show edit request history if submission status is pending but task progress status is not resubmitted
    const hideEditRequestHistory =
        submissionTask?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING && !submissionTask?.current_progress?.resubmitted

    return task ? (
        <Flex flexDirection="column" height="100%">
            <Text
                fontSize="0.9rem"
                fontWeight="bold"
                color={theme.colors.astronaut}
                pt={theme.spacing.xxs}
                textAlign="left"
            >
                {task.title}
            </Text>
            <Text
                fontSize="0.6rem"
                color={theme.colors.astronaut}
                textAlign="left"
                maxWidth="220px"
                sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
            >
                {t('taskType.stepNumberTitle', {
                    itemCount: stepIndex + 1,
                    firstItemCount: task.steps ? task.steps.length : 0,
                    lastItemCount: task.steps ? task.steps[stepIndex].data.title : 'Step Title',
                })}
            </Text>
            <Divider color="astronaut" sx={{ borderTopWidth: '2px' }} />
            {submissionTask && submissionTask.current_progress ? (
                submissionTask.status == SUBMISSION_TASK_STATUS_ENUM.NOT_SUBMITTED ? (
                    <NotDoneBlock />
                ) : submissionTask.status == SUBMISSION_TASK_STATUS_ENUM.COMPLETED ? (
                    <CompletedBlock
                        submissionTask={submissionTask}
                        handleVerify={() => handleClick(SUBMISSION_VIEW_ENUM.COMPLETED)}
                    />
                ) : submissionTask.status == SUBMISSION_TASK_STATUS_ENUM.PENDING ||
                  submissionTask.status == SUBMISSION_TASK_STATUS_ENUM.EDITS_REQUESTED ? (
                    isInternalViewOnlyStep(task.steps[stepIndex].type) && !profile.canSeeInWaitingListSubmission ? (
                        <InProgressBlock />
                    ) : (
                        <>
                            {submissionTask.current_progress ? (
                                <>
                                    <ViewEditRequestCommentsBlock hasResubmitted={hasResubmitted}>
                                        {!hideEditRequestHistory && (
                                            <>
                                                {submissionTask.current_progress.resubmitted && (
                                                    <ResubmittedIndicator />
                                                )}
                                                <EditRequestHistoryButton
                                                    submissionProgressUuid={submissionTask.current_progress.uuid}
                                                    partiposterId={partiposterId}
                                                    className="edit-request-history"
                                                />
                                            </>
                                        )}
                                    </ViewEditRequestCommentsBlock>

                                    <SubmissionContent
                                        submissionProgress={submissionTask.current_progress}
                                        step={task.steps[stepIndex]}
                                        showCaption={false}
                                        mt="0"
                                        photoPadding={theme.spacing.xxxs}
                                        showCustomStepTitle={false}
                                    />
                                </>
                            ) : null}
                            <Box my="auto" mx="auto" />
                            <CommentAndVoteSummary
                                submissionProgress={submissionTask.current_progress}
                                step={task.steps[stepIndex]}
                            />
                            {submissionTask.current_progress.pending_action != null ? (
                                <SubmissionActionWrapper>
                                    <RectangleButton
                                        size="m"
                                        color="neutral"
                                        form="outlined"
                                        label={t('button.underReview')}
                                        onClick={() => handleClick(SUBMISSION_VIEW_ENUM.FOR_REVIEW)}
                                        className="width-full"
                                    />
                                </SubmissionActionWrapper>
                            ) : isInternalViewOnlyStep(task.steps[stepIndex].type) &&
                              submissionTask.status == SUBMISSION_TASK_STATUS_ENUM.PENDING ? (
                                <SubmissionActionWrapper>
                                    <RectangleButton
                                        size="m"
                                        form="filled"
                                        onClick={() => handleClick(SUBMISSION_VIEW_ENUM.PENDING)}
                                        label={
                                            checkCanReviewSubmissionByStep(task.steps[stepIndex].type)
                                                ? t('button.verify', 'Verify')
                                                : t('button.viewDetail', 'View Details')
                                        }
                                        className="width-full"
                                    />
                                </SubmissionActionWrapper>
                            ) : submissionTask.status == SUBMISSION_TASK_STATUS_ENUM.EDITS_REQUESTED ? (
                                <SubmissionActionWrapper>
                                    <RectangleButton
                                        size="m"
                                        color="neutral"
                                        form="outlined"
                                        onClick={() => handleClick(SUBMISSION_VIEW_ENUM.EDITS_REQUESTED)}
                                        label={t('button.requestEdits')}
                                        className="width-full"
                                    />
                                </SubmissionActionWrapper>
                            ) : checkCanReviewSubmissionByStep(task.steps[stepIndex].type) ? (
                                <RejectOrVerify
                                    submissionUuid={submissionUuid}
                                    submissionProgressUuid={submissionTask.current_progress.uuid}
                                    step={task.steps[stepIndex]}
                                />
                            ) : (
                                <ViewDetailButton
                                    submissionProgressUuid={submissionTask.current_progress.uuid}
                                    step={task.steps[stepIndex]}
                                />
                            )}
                            {selectedCampaignSummary && (
                                <>
                                    <ReviewedByDisplay
                                        submissionProgress={submissionTask.current_progress}
                                        country={selectedCampaignSummary.country}
                                    />
                                    <SubmittedAtDisplay
                                        submissionProgress={submissionTask.current_progress}
                                        isTaskPending={submissionTask.status === SUBMISSION_TASK_STATUS_ENUM.PENDING}
                                        country={selectedCampaignSummary.country}
                                        mt={spacing[300]}
                                    />
                                </>
                            )}
                        </>
                    )
                ) : submissionTask.status == SUBMISSION_TASK_STATUS_ENUM.ACTION_REQUIRED ? (
                    <InProgressBlock />
                ) : (
                    <></>
                )
            ) : (
                <NotDoneBlock />
            )}
        </Flex>
    ) : null
}

const CarouselTaskList: React.FC<CarouselProps> = ({ submissionTasks, submissionUuid, partiposterId }) => {
    const tasks = useSelector(selectTasks)
    const showSlider = tasks.length > 4
    const hasResubmitted = submissionTasks.some(submission => submission?.current_progress?.resubmitted === true)
    // if not submit any submissions, need to check tasks to get not done status
    return (
        <CarouselWrapper
            naturalSlideWidth={174}
            naturalSlideHeight={293}
            totalSlides={tasks.length}
            visibleSlides={tasks.length > 3 ? 4 : tasks.length}
            isIntrinsicHeight={true}
            dragEnabled={showSlider}
        >
            <Slider
                className={`max-height ${showSlider ? 'slider' : undefined}`}
                classNameTray="max-height"
                classNameTrayWrap="max-height"
            >
                {tasks &&
                    tasks.map((task: TaskState, taskIndex: number) => {
                        const submissionTask =
                            submissionTasks && submissionTasks.length > 0
                                ? submissionTasks.find(t => t.task_uuid == task.uuid)
                                : undefined
                        if (submissionTask != undefined) {
                            const currentStepIndex =
                                task && submissionTask.current_progress
                                    ? task.steps.findIndex(
                                          (step: StepState) =>
                                              submissionTask.current_progress &&
                                              step.uuid == submissionTask.current_progress.step_uuid,
                                      )
                                    : 0
                            return (
                                <SlideWrapper key={submissionTask.task_uuid} index={taskIndex}>
                                    <TaskProgress
                                        submissionTask={submissionTask}
                                        task={task}
                                        currentStepIndex={currentStepIndex}
                                        submissionUuid={submissionUuid}
                                        partiposterId={partiposterId}
                                        hasResubmitted={hasResubmitted}
                                    />
                                </SlideWrapper>
                            )
                        } else {
                            return (
                                <SlideWrapper key={task.uuid} index={taskIndex}>
                                    <TaskProgress task={task} partiposterId={partiposterId} />
                                </SlideWrapper>
                            )
                        }
                    })}
            </Slider>
            {showSlider && (
                <ButtonBack className={'buttonBack'}>
                    <ChevronLeft stroke={theme.colors.dodgerBlue} />
                </ButtonBack>
            )}
            {showSlider && (
                <ButtonNext className={'buttonNext'}>
                    <ChevronRight stroke={theme.colors.dodgerBlue} />
                </ButtonNext>
            )}
        </CarouselWrapper>
    )
}

interface BatchProgressBlockProps {
    batchSubmission: BatchSubmissionState
}

const BatchProgressBlock: React.FC<BatchProgressBlockProps> = ({ batchSubmission }) => {
    const { submission_tasks, partiposterId } = batchSubmission
    return (
        <CarouselTaskList
            submissionTasks={submission_tasks}
            submissionUuid={batchSubmission.uuid}
            partiposterId={partiposterId}
        />
    )
}

export default BatchProgressBlock
